import { HoverDisplay } from '@/components/HoverDisplay';
import style from '@/theme/common.module.css';
import { CommunicationThreadTopic } from '@bariendo/lib/constants';
import { Flex, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconCornerUpRight } from '@tabler/icons-react';
import { useMemo } from 'react';
import { ChatTimelineGroup, MessageBoxType, TimelineNote } from '@/components/Timeline/types';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { DateTimeTooltip } from '@/components/Timeline/TimelineItems/DateTimeTooltip';
import { TextMessageTimelineItem } from '@/components/Timeline/TimelineItems/TextMessageTimelineItem';
import { TimelineItemProviders } from '@/components/Timeline/TimelineItems/TimelineItemProviders';
import { getPatientName } from '@/pages/PatientTimelinePage/services/timelinePatient';

interface ChatTimelineHeaderProps {
  group: ChatTimelineGroup;
}

export function ChatTimelineHeader({ group }: ChatTimelineHeaderProps) {
  const { patientContext, careTeamContext, partnerContext, messageBoxContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const { careTeam } = careTeamContext ?? {};
  const { getTopicLabel, setTopic, setMessageBoxVariant, setShowMessageInput, setAutofocusMessageBox } =
    messageBoxContext;

  const topic = group.items[0]?.resource?.partOf?.[0]?.resource?.topic?.coding?.[0]?.code as CommunicationThreadTopic;
  const patientName = useMemo(() => (patient ? getPatientName(patient) : undefined), [patient]);

  const partnerName = useMemo(() => {
    return partnerContext?.organization?.name || undefined;
  }, [partnerContext]);

  const handleReplyChat = (topic?: CommunicationThreadTopic) => {
    setTopic(topic || CommunicationThreadTopic.CLINICAL);
    setMessageBoxVariant(MessageBoxType.CHAT);
    setShowMessageInput(true);
    setAutofocusMessageBox(true);
  };

  return (
    <Group justify="space-between" align="center">
      <Group>
        <Flex gap="xs" align="center">
          <Stack gap={2}>
            <Flex gap="xs" align="center">
              <Text size="md" fw={700} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Chat with{' '}
                {careTeam?.participant?.length ? <TimelineItemProviders /> : (partnerName ?? patientName ?? 'Unknown')}
              </Text>
            </Flex>
            <Text variant="caption">{getTopicLabel(topic)} thread</Text>
          </Stack>
        </Flex>
      </Group>

      <Group gap="md" align="center">
        <HoverDisplay.Target>
          <UnstyledButton
            onClick={() => handleReplyChat(topic)}
            c="#145b6f"
            className={style.hoverable}
            h={'fit-content'}
            p="xs"
            style={{
              borderRadius: '100px',
            }}
          >
            <Group gap={4}>
              <IconCornerUpRight size={16} stroke={2} />
              <Text size="sm">Reply</Text>
            </Group>
          </UnstyledButton>
        </HoverDisplay.Target>
        <DateTimeTooltip date={group.timestamp} />
      </Group>
    </Group>
  );
}

interface ChatTimelineItemProps {
  group: ChatTimelineGroup;
  notes?: TimelineNote[];
  highlightRef?: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  searchTerm?: string;
}

export function ChatTimelineItem({
  group,
  notes = [],
  highlightRef,
  copyDirectLink,
  hasActiveFilters,
  handleNavigateToItem,
  searchTerm,
}: ChatTimelineItemProps) {
  return (
    <TextMessageTimelineItem
      group={group}
      notes={notes}
      highlightRef={highlightRef}
      copyDirectLink={copyDirectLink}
      hasActiveFilters={hasActiveFilters}
      handleNavigateToItem={handleNavigateToItem}
      header={<ChatTimelineHeader group={group} />}
      searchTerm={searchTerm}
    />
  );
}
